import React, { useEffect, useState, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { AddNewMeetingRoomBooking, CancelMeetingRoomBooking, GetMeetingRoomBookings, GetMeetingRooms, UpdateMeetingRoomBooking } from '../../services/backend';
import moment from 'moment';
import './style.css';
import { BottomUXSpace, ModalX } from '../../components';
import { Tooltip } from '@material-tailwind/react';
import { minutesToText, Notify, NotifyError } from '../../utils/functions';
import { CalendarIcon, ListIcon, TrashIcon, Edit2Icon, EyeIcon, Loader2, HelpCircle, ChevronDown, X } from 'lucide-react';
import { useAuth } from '../../services/contexts/authContext';
import { VERIFICATION_TYPES } from '../../utils/enums';
import { useConfirm } from '../../custom-hooks/confirm-dialog';
const localizer = momentLocalizer(moment)

const INITIAL_FORM_DATA_FOR_BOOKING = {
    user: '',
    meetingRoom: '',
    title: '',
    description: '',
    startDate: '',
    slot: '',
}

// Custom dropdown styled to match the Pulse design (bordered box + chevron + themed menu).
const FieldSelect = ({ label, required, disabled, value, placeholder, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const selected = options.find(o => String(o.value) === String(value));

    return (
        <div>
            {label && (
                <label className="mb-1.5 block text-xs font-semibold text-[#8C8175]">
                    {label}{required && <span className="text-brand"> *</span>}
                </label>
            )}
            <div ref={ref} className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen(o => !o)}
                    className="flex h-12 w-full items-center gap-2.5 rounded-xl border-[1.5px] border-[#E6DDCF] bg-cream px-3.5 text-left transition-colors focus:border-brand disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <div className="min-w-0 flex-1 truncate text-sm text-[#2B2420]">
                        {selected ? (selected.selected || selected.node || selected.label) : <span className="text-[#B7AC9E]">{placeholder || 'Select'}</span>}
                    </div>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-[#B7AC9E] transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && !disabled && (
                    <div className="absolute left-0 right-0 z-[60] mt-1 max-h-60 overflow-auto rounded-xl border border-[#ECE3D5] bg-white p-1 shadow-[0_20px_40px_-20px_rgba(40,22,8,0.45)]">
                        {options.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-[#8C8175]">No options available</div>
                        ) : options.map(o => (
                            <button
                                key={o.value}
                                type="button"
                                disabled={o.disabled}
                                onClick={() => { if (!o.disabled) { onChange(o.value); setOpen(false); } }}
                                className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors ${String(o.value) === String(value) ? 'bg-cream-input font-semibold text-brand' : 'text-[#2B2420] hover:bg-cream'} ${o.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                            >
                                {o.node || o.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const CustomerMeetingRoom = () => {
    const [events, setEvents] = useState([]);
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    const [bookingFormData, setBookingFormData] = useState(INITIAL_FORM_DATA_FOR_BOOKING);
    const [modalOpenForBooking, setModalOpenForBooking] = useState(false);
    const [loading, setLoading] = useState(true);
    const [meetingRooms, setMeetingRooms] = useState([]);
    const [refreshRequired, setRefreshRequired] = useState(0);
    const [view, setView] = useState('month');
    const [viewMode, setViewMode] = useState('calendar');
    const [loadingMutation, setLoadingMutation] = useState(false);
    const [isUpdatingBooking, setIsUpdatingBooking] = useState(false);
    const [isViewingPastBooking, setIsViewingPastBooking] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [originalBookingData, setOriginalBookingData] = useState(null);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [calVersion, setCalVersion] = useState(0);
    const isFirstLoad = useRef(true);
    const isDrillingRef = useRef(false);

    const { userDetails, refetchUserDetails } = useAuth();
    const { confirm, ConfirmationDialog } = useConfirm();

    useEffect(() => {
        const fetchMeetingRooms = async () => {
            try {
                if (isFirstLoad.current) setLoading(true);
                const response = await GetMeetingRooms();
                setMeetingRooms(response.data.meetingRooms);
            } catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
            finally {
                setLoading(false);
                isFirstLoad.current = false;
            }
        }
        const fetchEvents = async () => {
            try {
                const response = await GetMeetingRoomBookings();
                setMeetingRoomBookings(response.data.meetingRoomBookings);
                const events = response.data.meetingRoomBookings.filter((booking) => booking.status !== VERIFICATION_TYPES.CANCELLED).map((booking) => ({
                    id: booking._id,
                    title: booking.title,
                    start: new Date(booking.startDate),
                    end: new Date(booking.endDate),
                    resourceId: booking.meetingRoom?._id,
                    color: booking.meetingRoom?.idColor,
                    description: booking.description,
                    slot: booking.slot,
                    user: booking.user?._id,
                }));
                setEvents(events);
                // Bump the calendar key so react-big-calendar re-slots events
                // cleanly into their resource columns after a refetch (prevents
                // bookings briefly showing in the wrong room/color until remount).
                setCalVersion(v => v + 1);
            } catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
        }
        fetchMeetingRooms();
        fetchEvents();
    }, [refreshRequired]);

    const handleOnTimeSlotSelect = (slotInfo) => {
        if (view === 'day' || view === 'week') {
            setIsUpdatingBooking(false);
            setIsViewingPastBooking(false);
            setBookingFormData({
                ...INITIAL_FORM_DATA_FOR_BOOKING,
                startDate: slotInfo.start,
                meetingRoom: slotInfo.resourceId,
            });
            setModalOpenForBooking(true);
        }
        else if (view === 'month') {
            // Clicking anywhere in the day cell (not just the date number) drills into
            // Day view for that date, matching "Click a date to book a meeting room."
            // Switching `view` synchronously here unmounts react-big-calendar's month
            // grid while its internal mouse-selection handler is still finishing the
            // current click — a fast second click then hits a stale reference and
            // throws ("Cannot read properties of undefined (reading 'getDate')").
            // Deferring to the next tick lets RBC finish its own selection cycle first,
            // and the guard ignores any overlapping click while that's in flight.
            if (isDrillingRef.current) return;
            isDrillingRef.current = true;
            setTimeout(() => {
                setCalendarDate(slotInfo.start);
                setView('day');
                isDrillingRef.current = false;
            }, 0);
        }
    };

    const handleOnEventSelect = (event) => {
        if (event.user !== userDetails._id) {
            NotifyError('You cannot view or edit a booking that belongs to someone else.', 'Access Denied');
            return;
        }
        const formData = {
            user: event.user || '',
            meetingRoom: event.resourceId,
            title: event.title,
            description: event.description,
            startDate: event.start,
            slot: String(event.slot),
        };
        const isPastOrStarted = !moment(event.start).isAfter(moment());
        setBookingFormData(formData);
        setOriginalBookingData(formData);
        setIsUpdatingBooking(true);
        setIsViewingPastBooking(isPastOrStarted);
        setSelectedBookingId(event.id);
        setModalOpenForBooking(true);
    };

    const handleBookingFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!bookingFormData.meetingRoom) return NotifyError('Please select a meeting room', 'Validation Error');
            if (!bookingFormData.title) return NotifyError('Please enter a title', 'Validation Error');
            if (!bookingFormData.startDate) return NotifyError('Please select a start time', 'Validation Error');
            if (!bookingFormData.slot) return NotifyError('Please select a duration (slot)', 'Validation Error');

            // Confirm if booking is in the past
            if (!isUpdatingBooking && moment(bookingFormData.startDate).isBefore(moment())) {
                const confirmed = await confirm({
                    title: 'Create booking in the past?',
                    message: 'Are you sure you want to create a booking in the past? It will not be editable.',
                    confirmText: 'Yes, Create',
                    cancelText: 'No, Cancel',
                    confirmBtnColor: 'amber',
                });
                if (!confirmed) return;
            }

            setLoadingMutation(true);
            if (isUpdatingBooking) {

                const updatedMeetingRoomBooking = {
                    user: bookingFormData.user || userDetails._id,
                    meetingRoom: bookingFormData.meetingRoom,
                    title: bookingFormData.title,
                    description: bookingFormData.description,
                    startDate: bookingFormData.startDate,
                    slot: bookingFormData.slot,
                };
                await UpdateMeetingRoomBooking(selectedBookingId, updatedMeetingRoomBooking);
                setRefreshRequired(refreshRequired + 1);
                await refetchUserDetails();
                Notify('Meeting Room Booking Updated Successfully', 'success');
            } else {
                const newMeetingRoomBooking = {
                    user: bookingFormData.user,
                    meetingRoom: bookingFormData.meetingRoom,
                    title: `${userDetails.name} - ${bookingFormData.title}`,
                    description: `${userDetails.name} - ${userDetails.email} - ${bookingFormData.description || 'No Description'}`,
                    startDate: bookingFormData.startDate,
                    slot: bookingFormData.slot,
                };
                await AddNewMeetingRoomBooking(newMeetingRoomBooking);
                setRefreshRequired(refreshRequired + 1);
                await refetchUserDetails();
                Notify('Meeting Room Booked Successfully', 'success');
            }
            setModalOpenForBooking(false);
            setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
            setIsUpdatingBooking(false);
            setIsViewingPastBooking(false);
            setSelectedBookingId(null);
            setOriginalBookingData(null);
        }
        catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        }
        finally {
            setLoadingMutation(false);
        }
    }

    const handleCancelBooking = async (bookingId) => {
        const confirmation = await confirm({
            title: 'Are you sure you want to cancel this booking?',
            message: 'This action cannot be undone.',
            confirmText: 'Yes, Cancel',
            cancelText: 'No, Keep it',
            confirmBtnColor: 'red',
        });

        if (confirmation) {
            try {
                await CancelMeetingRoomBooking(bookingId);
                setRefreshRequired(refreshRequired + 1);
                await refetchUserDetails();
                Notify('Meeting Room Booking Cancelled Successfully', 'success');
            }
            catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
        }
    }

    const getAvailableTimeOptions = () => {
        const options = [];
        for (let i = 0; i < 48; i++) {
            const hours = Math.floor(i / 2);
            const minutes = (i % 2) * 30;
            const timeValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

            let isAvailable = true;
            if (bookingFormData.meetingRoom && bookingFormData.startDate) {
                const optionTime = moment(bookingFormData.startDate).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
                for (const event of events) {
                    if (event.resourceId === bookingFormData.meetingRoom) {
                        if (isUpdatingBooking && event.id === selectedBookingId) continue;
                        const eventStart = moment(event.start);
                        const eventEnd = moment(event.end);
                        if (optionTime.isSameOrAfter(eventStart) && optionTime.isBefore(eventEnd)) {
                            isAvailable = false;
                            break;
                        }
                    }
                }
            }
            if (isAvailable) {
                options.push({ value: timeValue, label: moment().hour(hours).minute(minutes).format('hh:mm A') });
            }
        }
        return options;
    };

    const getAvailableSlotOptions = () => {
        const defaultOptions = [
            { value: '30', label: '30 Minutes' },
            { value: '60', label: '1 Hour' },
            { value: '90', label: '1 Hour, 30 Minutes' },
            { value: '120', label: '2 Hours' }
        ];

        let remainingMinutes = Infinity;

        // 1. Calculate global pool remaining
        const globalMonthly = userDetails.meetingRoomQuota?.monthlyHours || 0;
        if (globalMonthly > 0) {
            let used = userDetails.meetingRoomQuota?.usedHours || 0;
            if (isUpdatingBooking && originalBookingData) {
                used = Math.max(0, used - originalBookingData.slot);
            }
            remainingMinutes = Math.min(remainingMinutes, globalMonthly - used);
        }

        // 2. Calculate per-room pool remaining
        if (bookingFormData.meetingRoom) {
            const rq = userDetails.roomQuotas?.find(q => q.room?.toString() === bookingFormData.meetingRoom?.toString());
            if (rq && rq.monthlyMinutes !== undefined) {
                let used = rq.usedMinutes || 0;
                if (isUpdatingBooking && originalBookingData && originalBookingData.meetingRoom?.toString() === bookingFormData.meetingRoom?.toString()) {
                    used = Math.max(0, used - originalBookingData.slot);
                }
                remainingMinutes = Math.min(remainingMinutes, rq.monthlyMinutes - used);
            }
        }

        const filtered = defaultOptions.filter(opt => parseInt(opt.value) <= remainingMinutes || (isUpdatingBooking && originalBookingData && opt.value === String(originalBookingData.slot)));

        if (filtered.length === 0) {
            return [{ value: 'no_limit', label: 'No Limit Left', disabled: true }];
        }
        return filtered;
    };

    const openBookingModal = (meetingRoomBooking, forceViewMode = false) => {
        const formData = {
            user: meetingRoomBooking.user?._id,
            meetingRoom: meetingRoomBooking.meetingRoom?._id,
            title: meetingRoomBooking.title,
            description: meetingRoomBooking.description,
            startDate: new Date(meetingRoomBooking.startDate),
            slot: String(meetingRoomBooking.slot),
        };

        setBookingFormData(formData);
        setOriginalBookingData(formData);
        setIsUpdatingBooking(true);
        setIsViewingPastBooking(forceViewMode || !moment(meetingRoomBooking.startDate).isAfter(moment()));
        setSelectedBookingId(meetingRoomBooking._id);
        setModalOpenForBooking(true);
    };

    const resetBookingModal = () => {
        setModalOpenForBooking(false);
        setIsUpdatingBooking(false);
        setIsViewingPastBooking(false);
        setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
        setSelectedBookingId(null);
        setOriginalBookingData(null);
    };

    if (loading && meetingRooms.length === 0) {
        return <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand" />
        </div>
    }

    const remainingMeetingRoomQuota = userDetails.meetingRoomQuota ?
        100 - (parseInt((userDetails.meetingRoomQuota.usedHours / userDetails.meetingRoomQuota.monthlyHours) * 100)) : 0;

    // Per-room quota for the currently selected room in the booking form
    const selectedRoomQuota = userDetails.roomQuotas?.find(
        rq => rq.room?.toString() === bookingFormData.meetingRoom?.toString()
    ) || null;

    const isFormChanged = isUpdatingBooking ?
        JSON.stringify({
            ...bookingFormData,
            startDate: new Date(bookingFormData.startDate).getTime()
        }) !== JSON.stringify({
            ...originalBookingData,
            startDate: new Date(originalBookingData?.startDate).getTime()
        })
        : true;

    const customerBookings = meetingRoomBookings.filter((booking) => booking.user?._id === userDetails?._id);

    const howToBook = (
        <div className="max-w-xs text-sm">
            <div className="mb-1 font-bold">How to book a Meeting Room?</div>
            <div>1. Click on the date in the calendar.</div>
            <div>2. Then click on the time slot you want to book.</div>
            <div>3. Select the meeting room you want to book.</div>
            <div>4. Enter the title, description and slot.</div>
            <div>5. Click on the Book button.</div>
        </div>
    );

    // Options for the custom dropdowns in the booking modal
    const roomNode = (room) => (
        <div className="flex items-center gap-2.5">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: room.idColor }} />
            <span className="truncate"><b className="font-semibold">{room.code}</b> · {room.floor?.building?.name}, {room.floor?.building?.branch?.name}</span>
        </div>
    );
    const roomOptions = meetingRooms.map(room => ({ value: room._id, node: roomNode(room), selected: roomNode(room), label: room.code }));
    const timeOptions = getAvailableTimeOptions();
    const slotOptions = getAvailableSlotOptions();
    const modalBarColor = meetingRooms.find(r => String(r._id) === String(bookingFormData.meetingRoom))?.idColor || '#BE5310';

    return (
        <div className="animate-fadeIn px-4 py-4 md:px-[34px] md:py-5">
            {/* Header */}
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="font-display text-[25px] font-bold text-ink">Meeting Room Bookings</h1>
                    <p className="mt-1 text-sm text-[#8C8175]">Click a date to book a meeting room.</p>
                </div>
                <Tooltip placement="bottom-end" content={howToBook}>
                    <button className="flex shrink-0 items-center gap-2 rounded-[11px] border border-[#ECE3D5] bg-white px-[15px] py-2.5 text-[12.5px] font-semibold text-[#3B2A1E] transition-colors hover:bg-cream">
                        <HelpCircle className="h-[18px] w-[18px] text-brand" />
                        How to book
                    </button>
                </Tooltip>
            </div>

            {/* Calendar / List toggle */}
            <div className="relative mb-3 grid w-full grid-cols-2 rounded-[14px] border border-[#ECE3D5] bg-white p-[6px]">
                <span
                    className="pointer-events-none absolute inset-y-[6px] left-[6px] w-[calc(50%-6px)] rounded-[10px] bg-brand shadow-[0_6px_14px_-8px_rgba(190,83,16,0.7)] transition-transform duration-[340ms] ease-[cubic-bezier(.4,0,.2,1)]"
                    style={{ transform: viewMode === 'list' ? 'translateX(100%)' : 'translateX(0%)' }}
                />
                {[{ key: 'calendar', label: 'Calendar', Icon: CalendarIcon }, { key: 'list', label: 'List', Icon: ListIcon }].map(({ key, label, Icon }) => {
                    const active = viewMode === key;
                    return (
                        <button
                            key={key}
                            onClick={() => setViewMode(key)}
                            className={`relative z-10 flex items-center justify-center gap-2 rounded-[10px] py-2.5 font-display text-sm font-semibold transition-colors ${active ? 'text-white' : 'text-[#6B675F] hover:text-ink'}`}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </button>
                    );
                })}
            </div>

            {viewMode === 'calendar' ? (
                <div className="animate-fadeIn">
                    <Calendar
                        key={calVersion}
                        date={calendarDate}
                        onNavigate={setCalendarDate}
                        localizer={localizer}
                        events={events}
                        eventPropGetter={(event) => {
                            return {
                                className: 'cursor-pointer',
                                style: {
                                    backgroundColor: event.color || '#BE5310',
                                    border: 'none',
                                }
                            }
                        }}
                        selectable={view === 'day' || view === 'week' || view === 'month'}
                        onSelectSlot={handleOnTimeSlotSelect}
                        onSelectEvent={handleOnEventSelect}
                        longPressThreshold={10}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 600 }}
                        view={view}
                        onView={setView}
                        resources={meetingRooms.map((meetingRoom) => ({
                            id: meetingRoom._id,
                            title: meetingRoom.code
                        }))}
                    />

                    {/* Room legend */}
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                        {meetingRooms.map((meetingRoom, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: meetingRoom.idColor }} />
                                <div className="text-xs text-[#6B675F]">{meetingRoom.number} ({meetingRoom.code})</div>
                            </div>
                        ))}
                    </div>

                    {/* Quota */}
                    <div className="mt-4 rounded-2xl border border-[#ECE3D5] bg-white p-5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-display text-base font-semibold text-ink">Meeting Room Quota — Organization Pool (Monthly)</span>
                            <span className="text-sm font-semibold text-brand">{remainingMeetingRoomQuota}% remaining</span>
                        </div>
                        <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-cream-100">
                            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.max(0, Math.min(100, remainingMeetingRoomQuota))}%` }} />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#6B675F]">
                            <div><span className="font-semibold text-ink">Total:</span> {(userDetails.meetingRoomQuota?.monthlyHours || 0) / 60} hrs</div>
                            <div><span className="font-semibold text-ink">Used:</span> {((userDetails.meetingRoomQuota?.usedHours || 0) / 60).toFixed(1)} hrs</div>
                            <div><span className="font-semibold text-ink">Remaining:</span> {(((userDetails.meetingRoomQuota?.monthlyHours || 0) - (userDetails.meetingRoomQuota?.usedHours || 0)) / 60).toFixed(1)} hrs</div>
                        </div>
                        {userDetails.roomQuotas?.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className="text-xs font-medium text-[#A89C8C]">Room limits:</span>
                                {meetingRooms.map(room => {
                                    const rq = userDetails.roomQuotas.find(q => q.room?.toString() === room._id?.toString());
                                    if (!rq || (rq.monthlyMinutes === 0 && rq.dailyMinutes === 0)) return null;
                                    const pct = rq.monthlyMinutes > 0 ? Math.round((rq.usedMinutes / rq.monthlyMinutes) * 100) : 0;
                                    const tone = pct >= 100 ? 'bg-red-50 text-red-700' : pct >= 75 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700';
                                    return (
                                        <span key={room._id} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${tone}`}>
                                            {room.code} · {((rq.monthlyMinutes - rq.usedMinutes) / 60).toFixed(1)} hr left
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="animate-fadeIn">
                    {customerBookings.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-[#E6DDCF] bg-white py-12 text-center text-[#8C8175]">
                            Click on a date in the calendar to book a meeting room.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-[#ECE3D5] bg-white p-4">
                            <table className="w-full min-w-max table-auto text-left">
                                <thead>
                                    <tr>
                                        <th colSpan={4} className="pb-3 text-xs font-semibold uppercase tracking-wide text-[#A89C8C]">List of all bookings</th>
                                    </tr>
                                    <tr className="border-b border-[#F1E9DC] text-[11px] font-bold uppercase tracking-wide text-[#A89C8C]">
                                        <th className="py-3">Meeting Room</th>
                                        <th className="py-3">Booking Time</th>
                                        <th className="py-3">Duration</th>
                                        <th className="py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customerBookings.map((meetingRoomBooking, index) => (
                                        <tr key={index} className="border-b border-[#F1E9DC] transition-colors hover:bg-cream">
                                            <td className="py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: meetingRoomBooking.meetingRoom?.idColor || 'gray' }} />
                                                    <span className="text-sm font-semibold text-ink">{meetingRoomBooking.meetingRoom?.code || 'N/A'}</span>
                                                </div>
                                                <div className="text-xs text-[#8C8175]">
                                                    {meetingRoomBooking.meetingRoom?.floor?.building?.name} - {meetingRoomBooking.meetingRoom?.floor?.building?.branch?.name}
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <div className="text-sm font-semibold text-ink">
                                                    {moment(meetingRoomBooking.startDate).format('LT')} - {moment(meetingRoomBooking.endDate).format('LT')}
                                                </div>
                                                <div className="text-xs text-[#8C8175]">{moment(meetingRoomBooking.startDate).format('ll')}</div>
                                            </td>
                                            <td className="py-3 text-sm text-[#6B675F]">{minutesToText(meetingRoomBooking.slot)}</td>
                                            <td className="py-3">
                                                {meetingRoomBooking.status !== VERIFICATION_TYPES.CANCELLED ? (
                                                    moment(meetingRoomBooking.startDate).isAfter(moment()) ? (
                                                        <div className="flex items-center gap-2">
                                                            <button onClick={() => openBookingModal(meetingRoomBooking)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-cream-input text-brand transition-colors hover:bg-[#F3D9C2]" aria-label="Edit booking">
                                                                <Edit2Icon className="h-4 w-4" />
                                                            </button>
                                                            <button onClick={() => handleCancelBooking(meetingRoomBooking._id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-red-600 transition-colors hover:bg-red-50" aria-label="Cancel booking">
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button onClick={() => openBookingModal(meetingRoomBooking, true)} className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B675F] transition-colors hover:bg-cream-input" aria-label="View booking">
                                                            <EyeIcon className="h-4 w-4" />
                                                        </button>
                                                    )
                                                ) : (
                                                    <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Cancelled</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <BottomUXSpace size={32} />
                        </div>
                    )}
                </div>
            )}

            {/* Booking modal (design-matched, our fields/options kept) */}
            <ModalX isOpen={modalOpenForBooking} onClose={() => setModalOpenForBooking(false)} showCloseButton={false} maxWidth="wide">
                <div className="-mx-5 -my-4">
                    {/* Room-colored top bar */}
                    <div className="h-1.5 w-full rounded-t-xl" style={{ backgroundColor: modalBarColor }} />
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 px-6 pt-5">
                        <div className="font-display text-lg font-semibold text-ink">
                            {isViewingPastBooking ? "Viewing a past booking" : isUpdatingBooking ? "Update Booking" : "Book Meeting Room"}
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="text-right">
                                <div className="font-display text-sm font-semibold text-ink">
                                    {moment(bookingFormData.startDate).format('h:mm A')} — {
                                        bookingFormData.slot ?
                                            moment(bookingFormData.startDate).add(bookingFormData.slot, 'minutes').format('h:mm A') :
                                            <>Slot {!isViewingPastBooking && <span className="text-brand">*</span>}</>
                                    }
                                </div>
                                <div className="mt-0.5 text-xs text-brand">{moment(bookingFormData.startDate).format('LL')}</div>
                            </div>
                            <button type="button" onClick={() => setModalOpenForBooking(false)} className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#3B2A1E] text-white transition-colors hover:bg-[#BE3B28]">
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleBookingFormSubmit} className="px-6 pb-6 pt-4">
                        <div className="space-y-3.5">
                            <FieldSelect
                                label="Select Meeting Room"
                                required={!isViewingPastBooking}
                                disabled={isViewingPastBooking}
                                value={bookingFormData.meetingRoom}
                                placeholder="Choose a room"
                                options={roomOptions}
                                onChange={(v) => { if (v !== bookingFormData.meetingRoom) setBookingFormData({ ...bookingFormData, meetingRoom: v, slot: '' }); }}
                            />

                            {bookingFormData.meetingRoom && (() => {
                                const roomLeftMin = selectedRoomQuota ? (selectedRoomQuota.monthlyMinutes - selectedRoomQuota.usedMinutes) : null;
                                const roomNoLimit = selectedRoomQuota && (selectedRoomQuota.monthlyMinutes === 0 || roomLeftMin < 30);
                                const monthlyLeftMin = (userDetails.meetingRoomQuota?.monthlyHours || 0) - (userDetails.meetingRoomQuota?.usedHours || 0);
                                return (
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        {selectedRoomQuota && (
                                            <div className="rounded-xl border border-[#BBD8DE] bg-[#E3EFF1] px-3.5 py-3">
                                                <div className="text-[11.5px] font-medium text-[#2A5C68]">Quota left for this room</div>
                                                <div className={`font-display text-lg font-bold ${roomNoLimit ? 'text-red-600' : 'text-[#2A5C68]'}`}>
                                                    {roomNoLimit ? 'No limit left' : `${(roomLeftMin / 60).toFixed(1)} hr`}
                                                </div>
                                                {selectedRoomQuota.dailyMinutes > 0 && (
                                                    <div className="mt-0.5 text-[11px] text-[#5E8577]">Daily: {(selectedRoomQuota.dailyMinutes / 60).toFixed(1)} hr</div>
                                                )}
                                            </div>
                                        )}
                                        <div className="rounded-xl border border-[#BBD8DE] bg-[#E3EFF1] px-3.5 py-3">
                                            <div className="text-[11.5px] font-medium text-[#2A5C68]">Your monthly limit</div>
                                            <div className={`font-display text-lg font-bold ${monthlyLeftMin < 30 ? 'text-red-600' : 'text-[#2A5C68]'}`}>
                                                {(monthlyLeftMin / 60).toFixed(1)} hr
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-[#8C8175]">
                                    Title {!isViewingPastBooking && <span className="text-brand">*</span>}
                                </label>
                                <input
                                    name="title"
                                    placeholder="e.g. HR sync"
                                    required={!isViewingPastBooking}
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.title}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, title: e.target.value })}
                                    className="h-[46px] w-full rounded-xl border-[1.5px] border-[#E6DDCF] bg-cream px-3.5 text-sm text-[#2B2420] outline-none transition-colors placeholder:text-[#B7AC9E] focus:border-brand disabled:opacity-60"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs font-semibold text-[#8C8175]">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Add details…"
                                    rows={3}
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.description}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, description: e.target.value })}
                                    className="w-full resize-none rounded-xl border-[1.5px] border-[#E6DDCF] bg-cream px-3.5 py-3 text-sm text-[#2B2420] outline-none transition-colors placeholder:text-[#B7AC9E] focus:border-brand disabled:opacity-60"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <FieldSelect
                                    label="Start Time"
                                    required={!isViewingPastBooking}
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.startDate ? moment(bookingFormData.startDate).format("HH:mm") : ""}
                                    placeholder="Select time"
                                    options={timeOptions}
                                    onChange={(v) => {
                                        const [hours, minutes] = v.split(':');
                                        const newStartDate = moment(bookingFormData.startDate).set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0, millisecond: 0 }).toDate();
                                        setBookingFormData({ ...bookingFormData, startDate: newStartDate });
                                    }}
                                />
                                <FieldSelect
                                    label="Slot"
                                    required={!isViewingPastBooking}
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.slot ? String(bookingFormData.slot) : ""}
                                    placeholder="Select slot"
                                    options={slotOptions}
                                    onChange={(v) => setBookingFormData(prev => ({ ...prev, slot: v }))}
                                />
                            </div>
                        </div>

                        <div className="mt-5 flex items-center justify-end gap-2.5">
                            {isUpdatingBooking && !isViewingPastBooking && (
                                <button
                                    type="button"
                                    onClick={() => handleCancelBooking(selectedBookingId).then(() => { setModalOpenForBooking(false); setIsUpdatingBooking(false); })}
                                    disabled={loadingMutation}
                                    className="mr-auto flex items-center gap-2 rounded-[11px] border-[1.5px] border-red-200 px-4 py-2.5 text-[13.5px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                                >
                                    <TrashIcon size={14} /> Cancel Booking
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={resetBookingModal}
                                disabled={loadingMutation}
                                className="rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-5 py-2.5 font-display text-[13.5px] font-semibold text-[#3B2A1E] transition-colors hover:bg-cream disabled:opacity-60"
                            >
                                Close
                            </button>
                            {!isViewingPastBooking && (
                                <button
                                    type="submit"
                                    disabled={loadingMutation || !isFormChanged || !bookingFormData.slot || bookingFormData.slot === 'no_limit' || !bookingFormData.title?.trim() || !bookingFormData.meetingRoom || !bookingFormData.startDate}
                                    className="flex items-center gap-2 rounded-[11px] bg-brand px-6 py-2.5 font-display text-[13.5px] font-semibold text-white shadow-[0_8px_16px_-10px_rgba(190,83,16,0.7)] transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {loadingMutation && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {isUpdatingBooking ? "Update Booking" : "Book"}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </ModalX>
            <ConfirmationDialog />
        </div>
    )
}

export default CustomerMeetingRoom;
